/**
 * @CODE:ACC-003-HOOKS-001
 * useFavorite Hook
 *
 * Custom hook for managing accommodation favorites
 * Features:
 * - Add/remove from user_favorites table
 * - Requires Supabase auth session
 * - Loading and error state management
 * - Track favorite status
 */

import { useState } from 'react';
import { getSupabaseClient } from '@/lib/supabase';

interface UseFavoriteReturn {
  isFavorited: boolean;
  isLoading: boolean;
  error: Error | null;
  addFavorite: () => Promise<void>;
  removeFavorite: () => Promise<void>;
  toggleFavorite: () => Promise<void>;
}

/**
 * Hook to manage accommodation favorites
 * @param accommodationId - The accommodation ID
 * @returns Object with favorite state and mutation functions
 */
export function useFavorite(accommodationId: number): UseFavoriteReturn {
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const addFavorite = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const client = getSupabaseClient();

      // Check auth session
      const { data: session, error: sessionError } = await client.auth.getSession();

      if (sessionError || !session.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // Insert into user_favorites
      const { error: insertError } = await client
        .from('user_favorites')
        .insert({
          user_id: session.session.user.id,
          accommodation_id: accommodationId,
        });

      if (insertError) {
        throw new Error(insertError.message);
      }

      setIsFavorited(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to add favorite');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFavorite = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const client = getSupabaseClient();

      // Check auth session
      const { data: session, error: sessionError } = await client.auth.getSession();

      if (sessionError || !session.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // Delete from user_favorites
      const { error: deleteError } = await client
        .from('user_favorites')
        .delete()
        .eq('user_id', session.session.user.id)
        .eq('accommodation_id', accommodationId);

      if (deleteError) {
        throw new Error(deleteError.message);
      }

      setIsFavorited(false);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to remove favorite');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFavorite = async () => {
    if (isFavorited) {
      await removeFavorite();
    } else {
      await addFavorite();
    }
  };

  return {
    isFavorited,
    isLoading,
    error,
    addFavorite,
    removeFavorite,
    toggleFavorite,
  };
}
